import { db, auth } from "@/lib/firebase/clientApp";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
  Timestamp,
  orderBy,
  limit,
} from "firebase/firestore";
import {
  SubscriptionTier,
  SubscriptionStatus,
  UserSubscription,
  MonthlyUsage,
  PlanChangeRequest,
  SUBSCRIPTION_LIMITS,
  SubscriptionDuration
} from "@/lib/firebase/types/subscription";

// Firestore 컬렉션 정의
export const subscriptionsCollection = collection(db, "subscriptions");
export const monthlyUsageCollection = collection(db, "monthlyUsage");
export const planChangeRequestsCollection = collection(db, "planChangeRequests");

// 사용자 구독 정보 가져오기
export async function getUserSubscription(userId: string): Promise<UserSubscription | null> {
  try {
    const subscriptionDoc = await getDoc(doc(subscriptionsCollection, userId));

    if (!subscriptionDoc.exists()) {
      // Firebase Auth에서 사용자 이메일 가져오기
      const currentUser = auth.currentUser;
      const userEmail = currentUser?.email || '';

      // 구독 정보가 없으면 기본 Standard 등급으로 생성
      const newSubscription: UserSubscription = {
        userId,
        userEmail,
        tier: SubscriptionTier.STANDARD,
        status: SubscriptionStatus.ACTIVE,
        startDate: new Date(),
        endDate: null,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await setDoc(doc(subscriptionsCollection, userId), {
        ...newSubscription,
        startDate: Timestamp.fromDate(newSubscription.startDate),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return newSubscription;
    }

    const data = subscriptionDoc.data();
    const subscription = {
      ...data,
      startDate: data.startDate?.toDate(),
      endDate: data.endDate?.toDate() || null,
      trialEndDate: data.trialEndDate?.toDate(),
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
    } as UserSubscription;

    // 구독 기간(endDate) 만료 확인 → Standard로 다운그레이드
    if (subscription.endDate && new Date() > subscription.endDate) {
      await updateDoc(doc(subscriptionsCollection, userId), {
        tier: SubscriptionTier.STANDARD,
        status: SubscriptionStatus.ACTIVE,
        endDate: null,
        updatedAt: serverTimestamp(),
      });
      subscription.tier = SubscriptionTier.STANDARD;
      subscription.endDate = null;
    }

    // 체험 기간(trialEndDate) 만료 확인 → Standard로 다운그레이드
    if (subscription.status === SubscriptionStatus.TRIAL && subscription.trialEndDate && new Date() > subscription.trialEndDate) {
      await updateDoc(doc(subscriptionsCollection, userId), {
        tier: SubscriptionTier.STANDARD,
        status: SubscriptionStatus.ACTIVE,
        trialEndDate: null,
        updatedAt: serverTimestamp(),
      });
      subscription.tier = SubscriptionTier.STANDARD;
      subscription.status = SubscriptionStatus.ACTIVE;
      subscription.trialEndDate = undefined;
    }

    return subscription;
  } catch (error) {
    console.error("Error getting user subscription:", error);
    throw error;
  }
}

// 월별 사용량 가져오기
export async function getMonthlyUsage(userId: string, year?: number, month?: number): Promise<MonthlyUsage> {
  const now = new Date();
  const targetYear = year || now.getFullYear();
  const targetMonth = month || now.getMonth() + 1;

  try {
    // 해당 년월의 사용량 문서 ID
    const usageId = `${userId}_${targetYear}_${targetMonth}`;
    const usageDoc = await getDoc(doc(monthlyUsageCollection, usageId));

    if (!usageDoc.exists()) {
      // 구독 정보에서 userEmail 가져오기
      let userEmail = '';
      const subscriptionDoc = await getDoc(doc(subscriptionsCollection, userId));
      if (subscriptionDoc.exists()) {
        userEmail = subscriptionDoc.data()?.userEmail || '';
      }

      // 사용량 문서가 없으면 새로 생성
      const newUsage: MonthlyUsage = {
        userId,
        userEmail,
        year: targetYear,
        month: targetMonth,
        projectsCreated: 0,
        storageUsed: 0,
        projects: [],
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await setDoc(doc(monthlyUsageCollection, usageId), {
        ...newUsage,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return newUsage;
    }

    const data = usageDoc.data();
    return {
      ...data,
      lastProjectDate: data.lastProjectDate?.toDate(),
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
    } as MonthlyUsage;
  } catch (error) {
    console.error("Error getting monthly usage:", error);
    throw error;
  }
}

// 프로젝트 생성 가능 여부 확인
export async function canCreateProject(userId: string): Promise<{
  canCreate: boolean;
  reason?: string;
  currentUsage?: number;
  limit?: number;
}> {
  try {
    const subscription = await getUserSubscription(userId);
    if (!subscription) {
      return { canCreate: false, reason: "구독 정보를 찾을 수 없습니다." };
    }

    // 구독 상태 확인
    if (subscription.status === SubscriptionStatus.EXPIRED) {
      return { canCreate: false, reason: "구독이 만료되었습니다." };
    }

    // 구독 기간(endDate) 만료 확인
    if (subscription.endDate && new Date() > subscription.endDate) {
      // 구독 기간 만료 → Standard로 다운그레이드
      await updateDoc(doc(subscriptionsCollection, userId), {
        tier: SubscriptionTier.STANDARD,
        status: SubscriptionStatus.ACTIVE,
        endDate: null,
        updatedAt: serverTimestamp(),
      });
      // 다운그레이드 후 Standard 권한으로 계속 진행
      subscription.tier = SubscriptionTier.STANDARD;
      subscription.endDate = null;
    }

    // 체험 기간 확인
    if (subscription.status === SubscriptionStatus.TRIAL && subscription.trialEndDate) {
      if (new Date() > subscription.trialEndDate) {
        // 체험 기간 만료 → Standard로 다운그레이드
        await updateDoc(doc(subscriptionsCollection, userId), {
          tier: SubscriptionTier.STANDARD,
          status: SubscriptionStatus.ACTIVE,
          trialEndDate: null,
          updatedAt: serverTimestamp(),
        });
        subscription.tier = SubscriptionTier.STANDARD;
        subscription.status = SubscriptionStatus.ACTIVE;
      }
    }

    // 월별 사용량 확인
    const usage = await getMonthlyUsage(userId);
    const limits = SUBSCRIPTION_LIMITS[subscription.tier];

    // 무제한인 경우
    if (limits.monthlyProjects === -1) {
      return { canCreate: true, currentUsage: usage.projectsCreated };
    }

    // 제한 확인
    if (usage.projectsCreated >= limits.monthlyProjects) {
      return {
        canCreate: false,
        reason: `이번 달 프로젝트 생성 한도(${limits.monthlyProjects}개)에 도달했습니다.`,
        currentUsage: usage.projectsCreated,
        limit: limits.monthlyProjects
      };
    }

    return {
      canCreate: true,
      currentUsage: usage.projectsCreated,
      limit: limits.monthlyProjects
    };
  } catch (error) {
    console.error("Error checking project creation permission:", error);
    return { canCreate: false, reason: "권한 확인 중 오류가 발생했습니다." };
  }
}

// 파일 크기 제한 확인
export function checkFileSizeLimit(subscription: UserSubscription, fileSizeMB: number): {
  isValid: boolean;
  reason?: string;
  limit?: number;
} {
  const limits = SUBSCRIPTION_LIMITS[subscription.tier];

  // 무제한인 경우
  if (limits.maxFileSize === -1) {
    return { isValid: true };
  }

  if (fileSizeMB > limits.maxFileSize) {
    return {
      isValid: false,
      reason: `파일 크기가 제한(${limits.maxFileSize}MB)을 초과합니다.`,
      limit: limits.maxFileSize
    };
  }

  return { isValid: true, limit: limits.maxFileSize };
}

// 옵션 사용 가능 여부 확인
export function checkOptionAvailability(
  subscription: UserSubscription,
  optionType: 'quality' | 'sceneSize' | 'autoColorCorrection',
  optionValue?: string
): boolean {
  const limits = SUBSCRIPTION_LIMITS[subscription.tier];

  switch (optionType) {
    case 'quality':
      if (!optionValue) return false;
      return limits.qualityOptions[optionValue as keyof typeof limits.qualityOptions] || false;

    case 'sceneSize':
      if (!optionValue) return false;
      return limits.sceneSizeOptions[optionValue as keyof typeof limits.sceneSizeOptions] || false;

    case 'autoColorCorrection':
      return limits.autoColorCorrection;

    default:
      return false;
  }
}

// 프로젝트 생성 시 사용량 업데이트
export async function incrementProjectUsage(userId: string, projectId: string): Promise<void> {
  try {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const usageId = `${userId}_${year}_${month}`;

    const usageDoc = await getDoc(doc(monthlyUsageCollection, usageId));

    if (usageDoc.exists()) {
      const data = usageDoc.data();
      await updateDoc(doc(monthlyUsageCollection, usageId), {
        projectsCreated: (data.projectsCreated || 0) + 1,
        projects: [...(data.projects || []), projectId],
        lastProjectDate: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } else {
      // 문서가 없으면 새로 생성
      await setDoc(doc(monthlyUsageCollection, usageId), {
        userId,
        year,
        month,
        projectsCreated: 1,
        storageUsed: 0,
        projects: [projectId],
        lastProjectDate: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error("Error incrementing project usage:", error);
    throw error;
  }
}

// Plan 변경 요청 생성
export async function createPlanChangeRequest(
  request: Omit<PlanChangeRequest, 'id' | 'requestDate' | 'status'>
): Promise<string> {
  try {
    if (!auth.currentUser) {
      throw new Error("User not authenticated");
    }

    // 설명이 최소 20자 이상인지 확인
    if (request.description.length < 20) {
      throw new Error("요청 내용은 최소 20자 이상이어야 합니다.");
    }

    const docRef = await addDoc(planChangeRequestsCollection, {
      ...request,
      userId: auth.currentUser.uid,
      userEmail: auth.currentUser.email,
      status: 'pending',
      requestDate: serverTimestamp(),
    });

    return docRef.id;
  } catch (error) {
    console.error("Error creating plan change request:", error);
    throw error;
  }
}

// 모든 Plan 변경 요청 가져오기 (관리자용)
export async function getAllPlanChangeRequests(
  filterStatus?: 'pending' | 'approved' | 'rejected'
): Promise<PlanChangeRequest[]> {
  try {
    let q = query(
      planChangeRequestsCollection,
      orderBy('requestDate', 'desc')
    );

    if (filterStatus) {
      q = query(q, where('status', '==', filterStatus));
    }

    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      requestDate: doc.data().requestDate?.toDate(),
      processedDate: doc.data().processedDate?.toDate(),
    })) as PlanChangeRequest[];
  } catch (error) {
    console.error("Error getting plan change requests:", error);
    throw error;
  }
}

// 사용자의 Plan 변경 요청 가져오기
export async function getUserPlanChangeRequests(userId: string): Promise<PlanChangeRequest[]> {
  try {
    const q = query(
      planChangeRequestsCollection,
      where('userId', '==', userId),
      orderBy('requestDate', 'desc')
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      requestDate: doc.data().requestDate?.toDate(),
      processedDate: doc.data().processedDate?.toDate(),
    })) as PlanChangeRequest[];
  } catch (error) {
    console.error("Error getting user plan change requests:", error);
    throw error;
  }
}

// 무료 체험 시작 (Pro 7일)
export async function startFreeTrial(userId: string): Promise<void> {
  try {
    const trialEndDate = new Date();
    trialEndDate.setDate(trialEndDate.getDate() + 7); // 7일 후

    await setDoc(doc(subscriptionsCollection, userId), {
      userId,
      tier: SubscriptionTier.PRO,
      status: SubscriptionStatus.TRIAL,
      startDate: Timestamp.fromDate(new Date()),
      endDate: null,
      trialEndDate: Timestamp.fromDate(trialEndDate),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error starting free trial:", error);
    throw error;
  }
}